import React, { useEffect, useState } from "react";
import { Form, Input, Button, message, Card, Spin, Alert, Radio, Switch } from "antd";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faFloppyDisk } from "@fortawesome/free-solid-svg-icons";
import tiktokAffiliateConfigApi from "../../../api/tiktokAffiliateConfigApi";

// Toggle luồng mua TikTok: 'showcase' (trưng bày cũ) | 'link' (mua qua link RioHub).
// App + web đọc file tĩnh /img/tiktok-affiliate-config.json (chỉ dùng mode + enabled).
// Đổi ở đây → app/web tự nhận, không cần build lại. creator/channel dùng cho backend
// tạo affiliate link (RioHub); api key nằm ở env, KHÔNG hiển thị ở đây.
function TikTokAffiliateConfig() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const res = await tiktokAffiliateConfigApi.getConfig();
                const cfg = res.data?.data || {};
                form.setFieldsValue({
                    mode: cfg.mode || "showcase",
                    enabled: !!cfg.enabled,
                    creatorUsername: cfg.creatorUsername || "",
                    channelLabel: cfg.channelLabel || "",
                    // Rỗng là giá trị HỢP LỆ (tắt dòng nhắc) → dùng ?? chứ không phải ||
                    bonusNote: cfg.bonusNote ?? "",
                });
            } catch {
                message.error("Không tải được cấu hình");
            } finally {
                setLoading(false);
            }
        })();
    }, [form]);

    const onFinish = async (values) => {
        setSaving(true);
        try {
            await tiktokAffiliateConfigApi.updateConfig({
                mode: values.mode,
                enabled: !!values.enabled,
                creatorUsername: (values.creatorUsername || "").trim(),
                channelLabel: (values.channelLabel || "").trim(),
                bonusNote: (values.bonusNote ?? "").trim(),
            });
            message.success("Đã lưu cấu hình mua TikTok");
        } catch (err) {
            message.error(err?.response?.data?.message || "Lưu thất bại");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="p-8 text-center">
                <Spin />
            </div>
        );
    }

    return (
        <div className="p-6 max-w-3xl">
            <div className="flex items-center gap-2 mb-1">
                <FontAwesomeIcon icon={faLink} className="text-rose-600" />
                <h1 className="text-xl font-bold m-0">Cấu hình mua sắm TikTok</h1>
            </div>
            <p className="text-sm text-content-3 mb-4">
                Chuyển đổi cách mua TikTok giữa <b>Trưng bày</b> (cũ) và{" "}
                <b>Mua qua link</b> (RioHub Affiliate). App/web tự nhận khi lưu, không cần
                build lại. Cron lấy đơn RioHub vẫn chạy độc lập với toggle này.
            </p>
            <Alert
                type="info"
                showIcon
                className="mb-5"
                message="API key RioHub nằm ở biến môi trường (env)"
                description="Trang này chỉ chỉnh mode/bật-tắt và creator/channel. Khoá API KHÔNG hiển thị/sửa ở đây vì lý do bảo mật."
            />
            <Form form={form} layout="vertical" onFinish={onFinish}>
                <Card size="small" className="mb-4" title="Chế độ mua">
                    <Form.Item name="mode" label="Luồng mua hiển thị cho người dùng" className="mb-3">
                        <Radio.Group>
                            <Radio.Button value="showcase">Trưng bày (cũ)</Radio.Button>
                            <Radio.Button value="link">Mua qua link (RioHub)</Radio.Button>
                        </Radio.Group>
                    </Form.Item>
                    <Form.Item
                        name="enabled"
                        label="Bật tính năng"
                        valuePropName="checked"
                        className="mb-0"
                        extra="Tắt = app/web dùng lại luồng trưng bày mặc định."
                    >
                        <Switch />
                    </Form.Item>
                </Card>

                <Card size="small" className="mb-4" title="RioHub (cho backend tạo link)">
                    <Form.Item
                        name="creatorUsername"
                        label="Creator username"
                        rules={[{ required: true, message: "Bắt buộc" }]}
                    >
                        <Input placeholder="longhousee2" allowClear />
                    </Form.Item>
                    <Form.Item
                        name="channelLabel"
                        label="Channel (nhãn nguồn traffic)"
                        rules={[
                            {
                                pattern: /^[A-Za-z0-9_-]{1,64}$/,
                                message: "Chỉ chữ/số/_/- tối đa 64 ký tự",
                            },
                        ]}
                        className="mb-0"
                    >
                        <Input placeholder="longhousee" allowClear />
                    </Form.Item>
                </Card>

                {/* Chữ hiện ở app/web/trang dự phòng khi sàn đang thưởng thêm hoa hồng
                    cho sản phẩm. Để ở đây thay vì set cứng trong app: đổi lời không phải
                    OTA app + build lại web. Xoá trắng = tắt hẳn dòng nhắc. */}
                <Card size="small" className="mb-4" title="Dòng nhắc khi sàn thưởng thêm hoa hồng">
                    <Form.Item
                        name="bonusNote"
                        label="Nội dung hiển thị"
                        extra="Hiện dưới dòng 'Hoàn tiền đến' khi sàn đang thưởng thêm cho sản phẩm đó. Để trống = không hiện gì. Tối đa 300 ký tự."
                        rules={[{ max: 300, message: "Tối đa 300 ký tự" }]}
                        className="mb-0"
                    >
                        <Input.TextArea
                            rows={3}
                            allowClear
                            placeholder="🔥 Thưởng thêm tối đa lên đến 15% điểm hoàn tiền, mua ngay để nhận ưu đãi!"
                        />
                    </Form.Item>
                </Card>

                <Button
                    type="primary"
                    htmlType="submit"
                    loading={saving}
                    icon={<FontAwesomeIcon icon={faFloppyDisk} className="mr-2" />}
                >
                    Lưu cấu hình
                </Button>
            </Form>
        </div>
    );
}

export default TikTokAffiliateConfig;
